import React, { useState, createContext, useContext } from 'react';
// Message types
export type MessageType = 'text' | 'confirmation' | 'calendar' | 'price-input' | 'options' | 'requirements-toggle' | 'requirements-display' | 'requirements-confirmation' | 'supplier-selection' | 'sourcing-summary'; // Added new message type for the artifact
export interface Message {
  id: number;
  type: MessageType;
  text: string;
  sender: 'ai' | 'user';
  timestamp: Date;
  data?: any; // Additional data for interactive components
}
// Event status types
type EventStatus = 'draft' | 'supplier_selection' | 'review' | 'live' | 'awarded' | 'complete';
// Supplier interface
interface Supplier {
  id: string;
  name: string;
  description: string;
  tags: string[];
  rating: number;
  selected: boolean;
}
// Sourcing event interface
interface SourcingEvent {
  id: string;
  status: EventStatus;
  requirements: {
    title: string;
    description: string;
    deadline?: Date | string; // Allow both Date and string
    budget?: number;
    requirementsText?: string; // Added for storing requirements text
    requirementsSource?: 'ai' | 'user'; // Added to track source of requirements
    specifications?: string; // Add specifications field to store technical specifications
  };
  suppliers: Supplier[];
}
interface SourcingContextType {
  sourcingEvent: SourcingEvent | null;
  setSourcingEvent: React.Dispatch<React.SetStateAction<SourcingEvent | null>>;
  createNewEvent: (title: string, description: string, deadline?: Date | string, budget?: number, requirementsText?: string, specifications?: string) => void;
  updateEventStatus: (status: EventStatus) => void;
  toggleSupplierSelection: (supplierId: string) => void;
  addSupplier: (supplier: Supplier) => void;
  addSuppliers: (suppliers: Supplier[]) => void;
  // Chat messages state
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  isAiTyping: boolean;
  setIsAiTyping: React.Dispatch<React.SetStateAction<boolean>>;
  currentWorkflow: {
    stage: 'initial' | 'analysis' | 'clarification' | 'requirements' | 'complete' | string;
    data: Record<string, any>;
  };
  setCurrentWorkflow: React.Dispatch<React.SetStateAction<{
    stage: 'initial' | 'analysis' | 'clarification' | 'requirements' | 'complete' | string;
    data: Record<string, any>;
  }>>;
}
const SourcingContext = createContext<SourcingContextType | undefined>(undefined);
export const SourcingProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [sourcingEvent, setSourcingEvent] = useState<SourcingEvent | null>(null);
  // Chat state
  const [messages, setMessages] = useState<Message[]>([{
    id: 1,
    type: 'text',
    text: "Hello! I'm the Prospeum Procurement AI. I can assist you with various tasks. How can I support you?",
    sender: 'ai',
    timestamp: new Date()
  }, {
    id: 2,
    type: 'options',
    text: 'Please select an option:',
    sender: 'ai',
    timestamp: new Date(Date.now() + 100),
    data: {
      options: [{
        id: 'sourcing',
        title: 'Create and Perform a Sourcing Event',
        description: 'Start a new sourcing process to find suppliers'
      }, {
        id: 'analyze',
        title: 'Analyze a Supplier',
        description: 'Get insights and details about a specific supplier'
      }, {
        id: 'summarize',
        title: 'Summarize all important tasks',
        description: 'View a summary of your pending tasks'
      }]
    }
  }]);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<{
    stage: 'initial' | 'analysis' | 'clarification' | 'requirements' | 'complete' | string;
    data: Record<string, any>;
  }>({
    stage: 'initial',
    data: {}
  });
  const createNewEvent = (title: string, description: string, deadline?: Date | string, budget?: number, requirementsText?: string, specifications?: string) => {
    // Enhanced debugging to see exactly what's being received
    console.log('CREATE EVENT RECEIVED VALUES:', {
      title,
      description,
      deadline,
      budget,
      requirementsText,
      specifications,
      deadlineType: deadline ? typeof deadline : 'undefined',
      deadlineIsDate: deadline instanceof Date,
      deadlineToString: deadline ? String(deadline) : 'undefined',
      budgetType: budget ? typeof budget : 'undefined',
      budgetToString: budget ? String(budget) : 'undefined'
    });
    // Ensure proper type handling
    let processedDeadline = deadline;
    let processedBudget = budget;
    // Handle deadline conversion if needed
    if (deadline && typeof deadline === 'string' && !isNaN(new Date(deadline).getTime())) {
      processedDeadline = new Date(deadline);
      console.log('Converted string deadline to Date:', processedDeadline);
    }
    // Handle budget conversion if needed
    if (budget !== undefined) {
      processedBudget = Number(budget);
      if (isNaN(processedBudget)) {
        processedBudget = undefined;
        console.error('Invalid budget value, setting to undefined');
      } else {
        console.log('Converted budget to number:', processedBudget);
      }
    }
    // CRITICAL FIX: Process specifications correctly
    // If specifications is provided, use it; otherwise, use requirementsText
    const processedSpecifications = specifications || requirementsText || '';
    // Create a new event with the processed values
    const newEvent: SourcingEvent = {
      id: `event-${Date.now()}`,
      status: 'supplier_selection',
      requirements: {
        title,
        description,
        deadline: processedDeadline,
        budget: processedBudget,
        requirementsText: requirementsText || processedSpecifications,
        specifications: processedSpecifications
      },
      suppliers: []
    };
    // Final verification of the created event
    console.log('NEW EVENT CREATED:', {
      id: newEvent.id,
      requirements: newEvent.requirements,
      deadlineInEvent: newEvent.requirements.deadline,
      deadlineType: typeof newEvent.requirements.deadline,
      deadlineIsDate: newEvent.requirements.deadline instanceof Date,
      budgetInEvent: newEvent.requirements.budget,
      budgetType: typeof newEvent.requirements.budget,
      requirementsText: newEvent.requirements.requirementsText,
      specifications: newEvent.requirements.specifications
    });
    setSourcingEvent(newEvent);
  };
  const updateEventStatus = (status: EventStatus) => {
    if (!sourcingEvent) return;
    setSourcingEvent({
      ...sourcingEvent,
      status
    });
  };
  const toggleSupplierSelection = (supplierId: string) => {
    if (!sourcingEvent) {
      console.error('Cannot toggle supplier: No active sourcing event');
      return;
    }
    console.log(`Context: Toggling selection for supplier: ${supplierId}`);
    // Check if the supplier exists in the context
    const supplierToToggle = sourcingEvent.suppliers.find(s => s.id === supplierId);
    if (!supplierToToggle) {
      console.error(`Context: Supplier with ID ${supplierId} not found`);
      return;
    }
    const currentState = supplierToToggle.selected || false;
    console.log(`Context: Supplier ${supplierToToggle.name} current selection: ${currentState}`);
    // Create a deep copy to ensure we're not directly mutating state
    const updatedSuppliers = sourcingEvent.suppliers.map(supplier => {
      if (supplier.id === supplierId) {
        const newSelected = !supplier.selected;
        console.log(`Context: Supplier ${supplier.name} selection changed from ${supplier.selected} to ${newSelected}`);
        return {
          ...supplier,
          selected: newSelected
        };
      }
      return supplier;
    });
    // Log the updated suppliers for debugging
    const selectedSuppliers = updatedSuppliers.filter(s => s.selected);
    console.log('Context: Updated suppliers selection:', {
      count: selectedSuppliers.length,
      names: selectedSuppliers.map(s => s.name),
      allSuppliers: updatedSuppliers.map(s => ({
        id: s.id,
        name: s.name,
        selected: s.selected
      }))
    });
    // Update the entire event atomically
    setSourcingEvent({
      ...sourcingEvent,
      suppliers: updatedSuppliers
    });
  };
  const addSuppliers = (suppliers: Supplier[]) => {
    if (!sourcingEvent) {
      console.error('Cannot add suppliers: No active sourcing event');
      return;
    }
    console.log('Context: Adding/updating suppliers:', suppliers.map(s => s.name));
    // Check if we're updating existing suppliers or adding new ones
    const existingSupplierIds = sourcingEvent.suppliers.map(s => s.id);
    const newSuppliers = suppliers.filter(s => !existingSupplierIds.includes(s.id));
    const updatingSuppliers = suppliers.filter(s => existingSupplierIds.includes(s.id));
    console.log('Context: New suppliers:', newSuppliers.map(s => s.name));
    console.log('Context: Updating suppliers:', updatingSuppliers.map(s => s.name));
    // Create a new array with updated and new suppliers
    const updatedSuppliers = [
    // Keep existing suppliers that aren't being updated
    ...sourcingEvent.suppliers.filter(s => !updatingSuppliers.find(us => us.id === s.id)),
    // Add updated suppliers with their existing selection state preserved
    ...updatingSuppliers.map(supplier => {
      const existingSupplier = sourcingEvent.suppliers.find(s => s.id === supplier.id);
      return {
        ...supplier,
        selected: existingSupplier?.selected || false
      };
    }),
    // Add completely new suppliers
    ...newSuppliers.map(supplier => ({
      ...supplier,
      selected: false // Ensure all new suppliers start as unselected
    }))];
    setSourcingEvent({
      ...sourcingEvent,
      suppliers: updatedSuppliers
    });
    console.log('Context: Updated suppliers list:', updatedSuppliers.map(s => ({
      id: s.id,
      name: s.name,
      selected: s.selected
    })));
  };
  const addSupplier = (supplier: Supplier) => {
    if (!sourcingEvent) return;
    setSourcingEvent({
      ...sourcingEvent,
      suppliers: [...sourcingEvent.suppliers, supplier]
    });
  };
  return <SourcingContext.Provider value={{
    sourcingEvent,
    setSourcingEvent,
    createNewEvent,
    updateEventStatus,
    toggleSupplierSelection,
    addSupplier,
    addSuppliers,
    // Chat state
    messages,
    setMessages,
    isAiTyping,
    setIsAiTyping,
    currentWorkflow,
    setCurrentWorkflow
  }} data-id="element-990">
      {children}
    </SourcingContext.Provider>;
};
export const useSourcing = () => {
  const context = useContext(SourcingContext);
  if (context === undefined) {
    throw new Error('useSourcing must be used within a SourcingProvider');
  }
  return context;
};